/**
 * Where the task system's live updates are. Normal requests go through the gateway, but a socket
 * cannot, so the gateway's `/realtime-config` names the live region's own AppSync host. It is
 * fetched once at startup and again on every reconnect, and kept here so that building the Apollo
 * client can stay synchronous.
 */
import { getDALTaskSystemEndpoint } from '../config';
import { dalLogger } from '../dalLogger';

/** The two fields of the `/realtime-config` response that web reads. */
export interface TaskSystemRealtimeConfig {
    readonly region: string;
    readonly graphqlHost: string;
}

export const REALTIME_DISCOVERY_TIMEOUT_MS = 5000;

const SUPPORTED_VERSION = 1;

const REGION_PATTERN = /^[a-z]{2}(-[a-z]+)+-\d$/;

/**
 * `standardDomainPattern` from aws-appsync-subscription-link@3.1.2,
 * lib/realtime-subscription-handshake-link.js. The library treats any URL that fails it as a
 * custom domain, appends `/realtime`, and still sends the participant's token there. The only
 * change is the escaped dot before `com`, which refuses more and never less.
 * realtimeDiscovery.test.ts holds this copy to the installed library.
 */
const STANDARD_APPSYNC_GRAPHQL_URL =
    /^https:\/\/\w{26}\.appsync-api\.\w{2}(?:(?:-\w{2,})+)-\d\.amazonaws\.com\/graphql$/i;

let cachedConfig: TaskSystemRealtimeConfig | null = null;
let pendingLoad: Promise<TaskSystemRealtimeConfig | null> | null = null;

/** `/realtime-config` on the endpoint's own origin, so each deploy slot discovers its own region. */
export const getTaskSystemRealtimeConfigUrl = (endpoint: string): string | null => {
    try {
        const url = new URL(endpoint);

        return url.protocol === 'https:' ? `${url.origin}/realtime-config` : null;
    } catch {
        return null;
    }
};

/** Whether the subscription library reads `https://<host>/graphql` as AppSync, not a custom domain. */
export const isStandardAppSyncGraphqlHost = (host: string): boolean =>
    STANDARD_APPSYNC_GRAPHQL_URL.test(`https://${host}/graphql`);

export const getCachedTaskSystemRealtimeConfig = (): TaskSystemRealtimeConfig | null => cachedConfig;

type ParsedConfig = { readonly config: TaskSystemRealtimeConfig } | { readonly reason: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const quote = (value: unknown): string => JSON.stringify(value) ?? 'undefined';

const parseConfig = (body: unknown): ParsedConfig => {
    if (!isRecord(body)) {
        return { reason: 'the response is not an object' };
    }

    const { version, region, graphqlHost } = body;

    if (version !== SUPPORTED_VERSION) {
        return { reason: `unsupported version ${quote(version)}` };
    }

    if (typeof region !== 'string' || !REGION_PATTERN.test(region)) {
        return { reason: `invalid region ${quote(region)}` };
    }

    if (typeof graphqlHost !== 'string' || !isStandardAppSyncGraphqlHost(graphqlHost)) {
        return { reason: `graphqlHost ${quote(graphqlHost)} is not an AppSync GraphQL host the library recognises` };
    }

    return { config: { region, graphqlHost } };
};

const describeFailure = (err: unknown, timedOut: boolean): string => {
    if (timedOut) {
        return `no answer within ${REALTIME_DISCOVERY_TIMEOUT_MS} ms`;
    }

    return `the request failed (${err instanceof Error ? err.message : String(err)})`;
};

const requestConfig = async (url: string): Promise<ParsedConfig> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REALTIME_DISCOVERY_TIMEOUT_MS);

    try {
        // No headers at all: the gateway refuses the CORS preflight a custom header would force.
        const response = await fetch(url, { credentials: 'omit', signal: controller.signal });

        if (!response.ok) {
            return { reason: `HTTP ${response.status}` };
        }

        const body: unknown = await response.json().catch(() => undefined);

        return body === undefined ? { reason: 'the response body is not JSON' } : parseConfig(body);
    } catch (err) {
        return { reason: describeFailure(err, controller.signal.aborted) };
    } finally {
        clearTimeout(timeout);
    }
};

const loadFrom = async (url: string): Promise<TaskSystemRealtimeConfig | null> => {
    dalLogger.info(`Fetching the task-system realtime config from ${url}`);

    const parsed = await requestConfig(url);

    if ('config' in parsed) {
        cachedConfig = parsed.config;
        dalLogger.info(
            `Task-system live updates are in ${parsed.config.region}, on ${parsed.config.graphqlHost} (from ${url})`
        );

        return cachedConfig;
    }

    dalLogger.error(`Could not use the task-system realtime config from ${url}: ${parsed.reason}`);

    if (cachedConfig) {
        dalLogger.warn(
            `Reusing the last known task-system realtime region, ${cachedConfig.region} on ${cachedConfig.graphqlHost}`
        );
    }

    return cachedConfig;
};

/**
 * Fetches `/realtime-config` and caches it. Never rejects: on any failure it logs why and returns
 * whatever is cached, which is `null` until one load has succeeded. Calls made while a load is
 * running share it.
 */
export const loadTaskSystemRealtimeConfig = (): Promise<TaskSystemRealtimeConfig | null> => {
    // No URL means no task-system endpoint in this build, so the DAL is off and there is nothing to say.
    const url = getTaskSystemRealtimeConfigUrl(getDALTaskSystemEndpoint());

    if (!url) {
        return Promise.resolve(cachedConfig);
    }

    pendingLoad ??= loadFrom(url).finally(() => {
        pendingLoad = null;
    });

    return pendingLoad;
};
